import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {
  Ban,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  Lock,
  MessageSquare,
  UserPlus,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import {
  useDroppedList,
  useLeaveReachOut,
  useReengageDropped,
} from '../../../services/onboardingBoard';
import { Chip, PAD_X, SectionLabel, ago, initialsOf } from './NurtureView';
import { ReachOutDialog, ReachOutLine, ReengagedBadge, reachOutSummary } from './reachOut';
import { VIEW_ONLY_TITLE } from './ownership';
import ReengagePicker, { reengageReady } from '../workspace/ReengagePicker';

// Dropped (Nic 2026-10-15): everyone we dropped, paginated on the server
// like Nurture. Unlike Nurture, nobody is pulled back on a whim: a row can
// be re-engaged ONLY when they reached back out (texted, called, booked,
// came through a form or a partner again). Those float to the top with what
// they did; a rep decides: Re-engage (their Signing Up, Book or sign up)
// or Leave dropped (with an optional note for the timeline). A stop reason
// ("Asked us to stop", "Texted STOP") still surfaces, flagged, and asks
// before re-engaging.

const STOP_REASONS = ['Asked us to stop contacting them', 'Texted STOP'];
const firstNameOf = (name) => String(name || '').split(/\s+/)[0] || null;

// lg+ is a table; below it each row folds into a small card.
const GRID =
  'lg:grid lg:grid-cols-[minmax(0,1.5fr)_7rem_minmax(0,1.8fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_8.5rem] lg:items-center lg:gap-x-6';

const Row = ({ r, onReengage, onLeave, busy, flash, canWork, viewOnly = false }) => {
  const dropped = r.droppedAt ? moment(r.droppedAt) : null;
  const ref = useRef(null);
  useEffect(() => {
    if (flash) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [flash]);
  const reasonText = (r.reason === 'Other' && r.reasonDetail) || r.reason || 'Dropped';
  return (
    <li
      ref={ref}
      className={cn(
        'group flex flex-col gap-2 py-3 hover:bg-slate-50/80 dark:hover:!bg-white/[0.03]',
        PAD_X,
        GRID,
        r.detectedAt &&
          'bg-sky-50/70 shadow-[inset_3px_0_0_#0ea5e9] hover:bg-sky-50 dark:!bg-sky-500/10 dark:hover:!bg-sky-500/15',
        flash &&
          '!bg-amber-50 shadow-[inset_0_0_0_2px_#f59e0b] dark:!bg-amber-400/10 dark:shadow-[inset_0_0_0_2px_#fbbf24]',
      )}
    >
      {/* Lead, and what they did if they came back */}
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
            r.detectedAt
              ? 'bg-sky-600 text-white'
              : 'bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-400',
          )}
        >
          {initialsOf(r.name)}
        </span>
        <div className="min-w-0">
          <div
            className={cn(
              'truncate text-sm font-semibold',
              r.detectedAt
                ? 'text-slate-800 dark:text-slate-100'
                : 'text-slate-600 dark:text-slate-300',
            )}
          >
            {r.name}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5">
            {r.detectedAt ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-sky-600 px-1.5 py-px text-[10.5px] font-semibold text-white">
                <MessageSquare size={10} /> Reached out {ago(r.detectedAt)}
              </span>
            ) : r.sourceLabel ? (
              <span className="truncate text-xs text-slate-400 dark:text-slate-500">
                {r.sourceLabel}
              </span>
            ) : null}
          </div>
          {r.detectedAt ? <ReachOutLine reachOut={r.reachOut} /> : null}
        </div>
      </div>

      {/* Times re-engaged, like Nurture (Nic 2026-10-15). */}
      <div className="hidden justify-center lg:flex">
        <ReengagedBadge r={r} />
      </div>

      {/* Why they were dropped: the reason is always easy to see. */}
      <div className="min-w-0 pl-11 lg:pl-0">
        <div className="flex min-w-0 items-center gap-1.5">
          {r.stopReason ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-rose-100 px-1.5 py-px text-[10.5px] font-bold text-rose-700 dark:!bg-rose-500/15 dark:text-rose-300">
              <Ban size={10} /> Stop
            </span>
          ) : null}
          <span
            className="line-clamp-2 text-[13px] leading-snug text-slate-700 dark:text-slate-200"
            title={reasonText}
          >
            {reasonText}
          </span>
        </div>
        <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
          {r.droppedBy ? `By ${firstNameOf(r.droppedBy)}` : 'By the system'}
        </div>
      </div>

      {/* Dropped: when. On small screens the owner rides along here. */}
      <div className="flex min-w-0 items-baseline gap-2 pl-11 lg:block lg:pl-0">
        <div className="whitespace-nowrap text-[13px] text-slate-700 dark:text-slate-200">
          {dropped ? dropped.format('MMM D') : '-'}
          {dropped ? (
            <span className="text-slate-400 dark:text-slate-500"> · {dropped.fromNow()}</span>
          ) : null}
        </div>
        <div className="truncate text-[11px] text-slate-400 dark:text-slate-500 lg:hidden">
          {r.isMine ? 'Your lead' : r.ownerName ? `${r.ownerName}'s lead` : 'No owner'}
        </div>
      </div>

      {/* Owner: whose lead it was when it was dropped. */}
      <div className="hidden min-w-0 items-center gap-2 lg:flex">
        {r.ownerName ? (
          <>
            <span
              aria-hidden
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                r.isMine
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
              )}
            >
              {initialsOf(r.ownerName)}
            </span>
            <span className="truncate text-[13px] text-slate-700 dark:text-slate-200" title={r.ownerName}>
              {r.isMine ? 'You' : r.ownerName}
            </span>
          </>
        ) : (
          <span className="text-[13px] italic text-slate-400 dark:text-slate-500">No owner</span>
        )}
      </div>

      {/* The decision, only for someone who reached back out. */}
      <div className="flex flex-col items-start gap-1 pl-11 lg:items-end lg:pl-0">
        {/* Admins and viewers see them greyed (Nic 2026-10-23). */}
        {r.canReengage && (canWork || viewOnly) ? (
          <>
            <button
              type="button"
              disabled={busy || viewOnly}
              onClick={() => onReengage(r)}
              title={
                viewOnly
                  ? VIEW_ONLY_TITLE
                  : 'Puts them on your Signing Up, under Follow-up > Book or sign up: book a follow-up or send the agreement today'
              }
              className={cn(
                'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border-[1px] px-3 text-xs font-semibold outline-none focus:outline-none disabled:opacity-50',
                'border-slate-200 !bg-white !text-slate-700 hover:!border-slate-900 hover:!bg-slate-900 hover:!text-white group-hover:!border-slate-900 group-hover:!bg-slate-900 group-hover:!text-white',
                'dark:!border-white/15 dark:!bg-transparent dark:!text-slate-200 dark:hover:!border-slate-100 dark:hover:!bg-slate-100 dark:hover:!text-slate-900 dark:group-hover:!border-slate-100 dark:group-hover:!bg-slate-100 dark:group-hover:!text-slate-900',
              )}
            >
              <UserPlus size={13} /> Re-engage
            </button>
            <button
              type="button"
              disabled={busy || viewOnly}
              title={viewOnly ? VIEW_ONLY_TITLE : undefined}
              onClick={() => onLeave(r)}
              className="whitespace-nowrap px-1 text-[11px] font-semibold text-slate-400 outline-none hover:text-slate-700 hover:underline focus:outline-none disabled:opacity-50 dark:text-slate-500 dark:hover:text-slate-200"
            >
              Leave dropped
            </button>
          </>
        ) : null}
      </div>
    </li>
  );
};

// q is the board's one search (already debounced). focus ({ leadId, n })
// comes from clicking a Dropped result in that search: filters reset and the
// row is ringed for a few seconds, like Nurture.
// viewer / reps (Nic 2026-10-23): the manager picks who gets a re-engage
// from reps; admins and viewers see it all greyed.
const DroppedView = ({ q = '', focus = null, onReengaged, canWork = false, keep = null, viewer = null, reps = [] }) => {
  const viewOnly = viewer?.canAct === false;
  const [pickTo, setPickTo] = useState('');
  // Page and chips (Nic 2026-10-20): the board keeps them (`keep`), so a tab
  // only on screen while open comes back the way it was left. Standalone, it
  // keeps them itself.
  const [localKeep, setLocalKeep] = useState({ page: 1, reason: '', mine: false, q: '' });
  const kept = keep ? keep.state : localKeep;
  const setKept = keep ? keep.setState : setLocalKeep;
  const { page, reason, mine } = kept;
  const setPage = (v) =>
    setKept((s) => ({ ...s, page: typeof v === 'function' ? v(s.page) : v }));
  const setReason = (v) => setKept((s) => ({ ...s, reason: v }));
  const setMine = (v) => setKept((s) => ({ ...s, mine: v }));
  const [flashId, setFlashId] = useState(null);
  const [leaving, setLeaving] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const list = useDroppedList({ page, q, reason, mine });
  const reengage = useReengageDropped();
  const leave = useLeaveReachOut();

  // A new search or filter starts at page 1; coming back to the tab with the
  // same search keeps the page. Compared with the last values seen, not a
  // first-run flag (branch QA 2026-10-21: React's dev double-run reset the
  // page every time).
  const lastFilters = useRef({ q: kept.q, reason, mine });
  useEffect(() => {
    const was = lastFilters.current;
    if (was.q === q && was.reason === reason && was.mine === mine) return;
    lastFilters.current = { q, reason, mine };
    setKept((s) => ({ ...s, page: 1, q }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, reason, mine]);

  useEffect(() => {
    if (!focus?.leadId) return undefined;
    setReason('');
    setMine(false);
    setFlashId(focus.leadId);
    const t = setTimeout(() => setFlashId(null), 4000);
    return () => clearTimeout(t);
    // Only a new pick from the search runs this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  const rows = list.data?.rows || [];
  const total = list.data?.total || 0;
  const counts = list.data?.counts || {};
  const reasonCounts = counts.reasons || {};
  const pageSize = list.data?.pageSize || 50;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);

  const reached = rows.filter((r) => r.detectedAt);
  const rest = rows.filter((r) => !r.detectedAt);
  const split = reached.length > 0 && rest.length > 0;

  const doReengage = async (row, confirmStop = false) => {
    const toUserId = viewer?.isManager ? pickTo : null;
    try {
      await reengage.mutateAsync({ leadId: row.leadId, confirmStop, toUserId });
      setConfirming(null);
      const to = toUserId ? reps.find((p) => p.userId === toUserId) : null;
      toast.success(
        to && toUserId !== viewer?.userId
          ? `${row.name} is on ${to.name}'s Signing Up, under Book or sign up.`
          : `${row.name} is on your Signing Up, under Book or sign up. Book a follow-up or send the agreement today, or they go to Nurture tonight.`,
      );
      onReengaged?.(row.leadId);
    } catch (e) {
      if (e?.response?.data?.code === 'confirm_stop') {
        setConfirming(row);
        return;
      }
      toast.error(e?.response?.data?.msg || "That lead couldn't be re-engaged.");
    }
  };
  // A stop reason asks first (Nic 2026-10-15): show why they were dropped.
  // The manager picks who gets them in the same dialog (Nic 2026-10-23).
  const onReengage = (row) => {
    if (row.stopReason || viewer?.isManager) {
      setPickTo('');
      setConfirming(row);
      return;
    }
    doReengage(row);
  };

  const leaveIt = async (note) => {
    if (!leaving) return;
    try {
      await leave.mutateAsync({ leadId: leaving.leadId, as: 'dropped', note });
      toast.success(`${leaving.name} stays dropped.`);
      setLeaving(null);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't go through.");
    }
  };

  // Reason chips in the server's drop-reason order, only the ones in use
  // (plus the picked one); stop reasons get the red dot.
  const reasonChips = Object.keys(reasonCounts)
    .filter((k) => reasonCounts[k] > 0 || reason === k)
    .sort((a, b) => reasonCounts[b] - reasonCounts[a]);

  const busy = reengage.isLoading || leave.isLoading;
  const confirmSummary = confirming ? reachOutSummary(confirming.reachOut)?.text : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Filters */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="os-scroll -mx-1 flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto px-1 py-0.5">
          <Chip active={!reason} onClick={() => setReason('')} label="All" count={counts.all ?? total} />
          {counts.reached_out > 0 || reason === 'reached_out' ? (
            <Chip
              active={reason === 'reached_out'}
              onClick={() => setReason('reached_out')}
              label="Reached out"
              count={counts.reached_out || 0}
              dot="bg-sky-500"
              tone="sky"
            />
          ) : null}
          {reasonChips.map((k) => (
            <Chip
              key={k}
              active={reason === k}
              onClick={() => setReason(reason === k ? '' : k)}
              label={k}
              count={reasonCounts[k] || 0}
              dot={STOP_REASONS.includes(k) ? 'bg-rose-500' : 'bg-slate-400'}
            />
          ))}
        </div>
        <div
          role="group"
          aria-label="Whose leads"
          className="flex shrink-0 items-center rounded-full bg-slate-200/70 p-0.5 dark:!bg-white/10"
        >
          {[
            { v: false, label: 'Everyone' },
            { v: true, label: 'Mine' },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              aria-pressed={mine === o.v}
              onClick={() => setMine(o.v)}
              className={cn(
                'h-6 rounded-full px-3 text-xs font-semibold outline-none focus:outline-none',
                mine === o.v
                  ? 'bg-white text-slate-800 shadow-sm dark:!bg-slate-100 dark:!text-slate-900'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* The rule, and paging */}
      <div className="mb-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Info size={13} className="shrink-0 text-slate-400" />
          Dropped people come back only when they reach out. Re-engage them, or leave them dropped.
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Lock size={12} className="shrink-0 text-slate-400" />
          Contact details stay hidden unless you re-engage.
        </span>
        <div className="ml-auto flex items-center gap-1">
          <span className="whitespace-nowrap tabular-nums">
            {total ? (
              <>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {from}-{to}
                </span>{' '}
                of {total}
              </>
            ) : (
              '0'
            )}
          </span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            aria-label="Next page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="os-scroll relative min-h-0 flex-1 overflow-y-auto rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
        <div
          className={cn(
            'sticky top-0 z-10 hidden border-b-[1px] border-slate-100 bg-white/95 py-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-400 backdrop-blur dark:!border-white/10 dark:!bg-surface-card/95 dark:text-slate-500 lg:grid',
            PAD_X,
            GRID,
          )}
        >
          <div>Lead</div>
          <div className="text-center">Re-engaged</div>
          <div>Why dropped</div>
          <div>Dropped</div>
          <div>Owner</div>
          <div className="text-right">
            {list.isFetching && !list.isLoading ? (
              <Loader2 size={12} className="ml-auto animate-spin" />
            ) : null}
          </div>
        </div>

        {list.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : list.isError ? (
          <div className="flex h-40 items-center justify-center text-sm font-semibold text-rose-500">
            {list.error?.response?.data?.msg || "Dropped couldn't be loaded."}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-1 text-sm text-slate-400">
            <div className="font-semibold text-slate-500 dark:text-slate-400">
              {q ? (
                <span className="font-medium italic text-sky-700 dark:text-sky-300">No matches for this search</span>
              ) : reason || mine ? (
                'Nobody matches.'
              ) : (
                'Nobody has been dropped.'
              )}
            </div>
            {reason || mine ? (
              <button
                type="button"
                onClick={() => {
                  setReason('');
                  setMine(false);
                }}
                className="text-xs font-semibold text-sky-600 outline-none hover:underline focus:outline-none dark:text-sky-400"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : (
          <ul
            aria-label="Dropped"
            className={cn(
              'mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]',
              list.isFetching && !list.isLoading && 'opacity-70',
            )}
          >
            {split ? <SectionLabel tone="sky">Reached out</SectionLabel> : null}
            {reached.map((r) => (
              <Row
                key={r.leadId}
                r={r}
                onReengage={onReengage}
                viewOnly={viewOnly}
                onLeave={setLeaving}
                busy={busy}
                flash={flashId === r.leadId}
                canWork={canWork}
              />
            ))}
            {split ? <SectionLabel>Everyone else</SectionLabel> : null}
            {rest.map((r) => (
              <Row
                key={r.leadId}
                r={r}
                onReengage={onReengage}
                viewOnly={viewOnly}
                onLeave={setLeaving}
                busy={busy}
                flash={flashId === r.leadId}
                canWork={canWork}
              />
            ))}
          </ul>
        )}
      </div>

      <ReachOutDialog
        open={!!leaving}
        title={leaving ? `Leave ${leaving.firstName || leaving.name} dropped?` : ''}
        noteLabel="Note for their timeline (optional)"
        notePlaceholder="e.g. Texted to say they're not interested"
        confirmLabel="Leave dropped"
        busy={leave.isLoading}
        onConfirm={leaveIt}
        onClose={() => setLeaving(null)}
      >
        Their reach-out is answered and they stay dropped. If they reach out
        again, they come back to the top.
      </ReachOutDialog>

      <ReachOutDialog
        open={!!confirming}
        title={confirming ? `Re-engage ${confirming.firstName || confirming.name}?` : ''}
        confirmLabel={confirming?.stopReason ? 'Re-engage anyway' : 'Re-engage'}
        tone={confirming?.stopReason ? 'rose' : 'slate'}
        busy={reengage.isLoading}
        confirmDisabled={!reengageReady(viewer, pickTo)}
        onConfirm={() => doReengage(confirming, !!confirming?.stopReason)}
        onClose={() => setConfirming(null)}
      >
        {confirming && !confirming.stopReason ? (
          <>Who gets them? They go on that rep&apos;s Signing Up, under Book or sign up.</>
        ) : null}
        {confirming?.stopReason ? (
          <>
            They were dropped because:{' '}
            <span className="font-semibold text-rose-700 dark:text-rose-300">
              {confirming.reason}
            </span>
            .
            {confirmSummary ? (
              <>
                {' '}Since then: <span className="font-semibold">{confirmSummary}</span>.
              </>
            ) : null}{' '}
            Only re-engage if they clearly want to hear from us.
          </>
        ) : null}
        <ReengagePicker
          viewer={viewer}
          value={pickTo}
          onChange={setPickTo}
          options={reps.map((p) => ({ value: p.userId, label: p.name }))}
          className="mt-3 [&>*]:w-full"
        />
      </ReachOutDialog>
    </div>
  );
};

export default DroppedView;
